import Phaser from 'phaser';
import { t } from '../../i18n';

export default class UIScene extends Phaser.Scene {
  constructor() {
    super({ key: 'UIScene', active: false });
  }

  create() {
    const { width, height } = this.scale;

    this.volume = 100;
    this.drops = 0;
    this.showTouch = false;

    // Permite dirección + salto/compresión simultáneamente en Android.
    this.input.addPointer(3);

    this.createHud(width);
    this.createLevelBanner(width);
    this.createToast(width, height);
    this.setupTouchControls();
    this.setupOverlays();

    this.registerGameEvents();

    this.scale.on('resize', this.handleResize, this);

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.cleanup();
    });
  }

  createHud(width) {
    // Barra de volumen.
    this.volumeBg = this.add
      .rectangle(24, 24, 260, 22, 0x000000, 0.45)
      .setOrigin(0, 0)
      .setStrokeStyle(1, 0x22d3ee, 0.6);

    this.volumeFill = this.add
      .rectangle(28, 28, 252, 14, 0x22d3ee, 0.9)
      .setOrigin(0, 0);

    this.volumeLabel = this.add.text(
      28,
      50,
      `${t('level.volume')}  100`,
      {
        fontFamily: 'Fraunces, serif',
        fontSize: '16px',
        color: '#e0f7ff',
      }
    );

    // Contador de gotas.
    this.dropIcon = this.add
      .image(width - 90, 32, 'droplet')
      .setDisplaySize(24, 24);

    this.dropsText = this.add.text(
      width - 74,
      20,
      `${t('level.drops')}: 0`,
      {
        fontFamily: 'Fraunces, serif',
        fontSize: '18px',
        color: '#a5f3fc',
      }
    );

    // Botón de pausa.
    this.pauseBtn = this.add
      .rectangle(width - 90, 76, 50, 40, 0x0f172a, 0.7)
      .setStrokeStyle(1, 0x22d3ee, 0.7)
      .setInteractive({ useHandCursor: true });

    this.pauseBtnIcon = this.add
      .text(width - 90, 76, '❚❚', {
        fontSize: '18px',
        color: '#a5f3fc',
      })
      .setOrigin(0.5);

    this.pauseBtn.on('pointerdown', () => {
      this.pauseBtn.setFillStyle(0x0891b2, 0.95);
    });

    this.pauseBtn.on('pointerup', () => {
      this.pauseBtn.setFillStyle(0x0f172a, 0.7);
      this.game.events.emit('ui:pauseRequest');
    });

    this.pauseBtn.on('pointerupoutside', () => {
      this.pauseBtn.setFillStyle(0x0f172a, 0.7);
    });
  }

  createLevelBanner(width) {
    this.levelBanner = this.add
      .text(width / 2, 90, t('level.name'), {
        fontFamily: 'Fraunces, serif',
        fontSize: '28px',
        color: '#ecfeff',
        stroke: '#0e7490',
        strokeThickness: 2,
      })
      .setOrigin(0.5)
      .setAlpha(0);

    this.tweens.add({
      targets: this.levelBanner,
      alpha: 1,
      duration: 700,
      yoyo: true,
      hold: 2200,
      onComplete: () => {
        this.levelBanner.setVisible(false);
      },
    });
  }

  createToast(width, height) {
    this.toast = this.add
      .text(width / 2, height - 80, '', {
        fontFamily: 'Fraunces, serif',
        fontSize: '22px',
        color: '#ecfeff',
        backgroundColor: '#0e7490',
        padding: {
          x: 16,
          y: 8,
        },
      })
      .setOrigin(0.5)
      .setAlpha(0);
  }

  registerGameEvents() {
    this.onVolumeUpdate = (value) => this.setVolume(value);
    this.onDropsUpdate = (value) => this.setDrops(value);
    this.onToast = (key) => this.showToast(t(key));
    this.onShowTouchControls = (show) => this.toggleTouchControls(show);
    this.onVictory = (stats) => this.showVictory(stats);
    this.onDefeat = () => this.showDefeat();
    this.onShowPause = () => this.showPause();
    this.onHidePause = () => this.hidePause();
    this.onHideDefeat = () => this.hideDefeat();

    this.game.events.on('volume:update', this.onVolumeUpdate);
    this.game.events.on('drops:update', this.onDropsUpdate);
    this.game.events.on('ui:toast', this.onToast);
    this.game.events.on(
      'ui:showTouchControls',
      this.onShowTouchControls
    );
    this.game.events.on('level:victory', this.onVictory);
    this.game.events.on('level:defeat', this.onDefeat);
    this.game.events.on('ui:showPause', this.onShowPause);
    this.game.events.on('ui:hidePause', this.onHidePause);
    this.game.events.on('ui:hideDefeat', this.onHideDefeat);
  }

  setVolume(value) {
    this.volume = value;

    const width = Math.max(0, (value / 100) * 252);
    this.volumeFill.width = width;

    const color =
      value > 60
        ? 0x22d3ee
        : value > 30
          ? 0xfbbf24
          : 0xef4444;

    this.volumeFill.fillColor = color;
    this.volumeLabel.setText(
      `${t('level.volume')}  ${Math.round(value)}`
    );
  }

  setDrops(value) {
    this.drops = value;
    this.dropsText.setText(`${t('level.drops')}: ${value}`);
  }

  showToast(text) {
    this.toast
      .setText(text)
      .setAlpha(0)
      .setVisible(true);

    this.tweens.killTweensOf(this.toast);

    this.tweens.add({
      targets: this.toast,
      alpha: 1,
      duration: 200,
      yoyo: true,
      hold: 1600,
    });
  }

  setupTouchControls() {
    const { width, height } = this.scale;

    const buttonColor = 0x0f172a;
    const buttonStroke = 0x22d3ee;

    const createButton = (x, y, radius, label) => {
      const background = this.add
        .circle(x, y, radius, buttonColor, 0.55)
        .setStrokeStyle(2, buttonStroke, 0.7)
        .setInteractive({
          useHandCursor: true,
        });

      const text = this.add
        .text(x, y, label, {
          fontFamily: 'Fraunces, serif',
          fontSize: `${Math.floor(radius * 0.7)}px`,
          color: '#a5f3fc',
        })
        .setOrigin(0.5);

      return {
        background,
        text,
        activePointerId: null,
      };
    };

    this.touchGroup = this.add
      .container(0, 0)
      .setDepth(900)
      .setVisible(false);

    this.leftButton = createButton(
      80,
      height - 90,
      46,
      '◀'
    );

    this.rightButton = createButton(
      190,
      height - 90,
      46,
      '▶'
    );

    this.jumpButton = createButton(
      width - 90,
      height - 130,
      52,
      '⤒'
    );

    this.compressButton = createButton(
      width - 200,
      height - 90,
      46,
      '⤓'
    );

    this.touchButtons = [
      {
        button: this.leftButton,
        key: 'left',
        jump: false,
      },
      {
        button: this.rightButton,
        key: 'right',
        jump: false,
      },
      {
        button: this.jumpButton,
        key: 'jump',
        jump: true,
      },
      {
        button: this.compressButton,
        key: 'compress',
        jump: false,
      },
    ];

    this.touchGroup.add([
      this.leftButton.background,
      this.leftButton.text,
      this.rightButton.background,
      this.rightButton.text,
      this.jumpButton.background,
      this.jumpButton.text,
      this.compressButton.background,
      this.compressButton.text,
    ]);

    this.touchButtons.forEach(({ button, key, jump }) => {
      this.bindTouchButton(button, key, jump);
    });

    // Libera correctamente cada dedo aunque termine fuera del botón.
    this.globalPointerUpHandler = (pointer) => {
      this.touchButtons.forEach(({ button, key }) => {
        if (button.activePointerId === pointer.id) {
          this.releaseTouchButton(button, key, pointer);
        }
      });
    };

    this.input.on(
      'pointerup',
      this.globalPointerUpHandler
    );

    // Seguridad si el navegador cancela o pierde los toques.
    this.gameOutHandler = () => {
      this.releaseAllTouchControls();
    };

    this.input.on('gameout', this.gameOutHandler);
  }

  bindTouchButton(button, key, isJumpButton = false) {
    button.background.on('pointerdown', (pointer) => {
      // El botón ya está siendo controlado por otro dedo.
      if (
        button.activePointerId !== null &&
        button.activePointerId !== pointer.id
      ) {
        return;
      }

      button.activePointerId = pointer.id;

      button.background
        .setFillStyle(0x0f172a, 0.9)
        .setScale(0.94);

      const payload = {
        [key]: true,
      };

      if (isJumpButton) {
        payload.jumpJustPressed = true;
      }

      this.game.events.emit(
        'ui:mobileInput',
        payload
      );
    });

    button.background.on('pointerup', (pointer) => {
      this.releaseTouchButton(button, key, pointer);
    });

    button.background.on(
      'pointerupoutside',
      (pointer) => {
        this.releaseTouchButton(button, key, pointer);
      }
    );
  }

  releaseTouchButton(button, key, pointer = null) {
    if (
      pointer &&
      button.activePointerId !== null &&
      button.activePointerId !== pointer.id
    ) {
      return;
    }

    button.activePointerId = null;

    button.background
      .setFillStyle(0x0f172a, 0.55)
      .setScale(1);

    this.game.events.emit('ui:mobileInput', {
      [key]: false,
    });
  }

  releaseAllTouchControls() {
    if (!this.touchButtons) return;

    this.touchButtons.forEach(({ button }) => {
      button.activePointerId = null;

      button.background
        .setFillStyle(0x0f172a, 0.55)
        .setScale(1);
    });

    this.game.events.emit('ui:mobileInput', {
      left: false,
      right: false,
      jump: false,
      jumpJustPressed: false,
      compress: false,
    });
  }

  toggleTouchControls(show) {
    this.showTouch = Boolean(show);

    if (this.touchGroup) {
      this.touchGroup.setVisible(this.showTouch);
    }

    if (!this.showTouch) {
      this.releaseAllTouchControls();
    }
  }

  setupOverlays() {
    const { width, height } = this.scale;

    this.setupPauseOverlay(width, height);
    this.setupVictoryOverlay(width, height);
    this.setupDefeatOverlay(width, height);
  }

  setupPauseOverlay(width, height) {
    this.pauseOverlay = this.add
      .container(0, 0)
      .setDepth(1000)
      .setVisible(false);

    const dim = this.add.rectangle(
      width / 2,
      height / 2,
      width,
      height,
      0x020617,
      0.75
    );

    const title = this.add
      .text(
        width / 2,
        height / 2 - 140,
        t('pause.title'),
        {
          fontFamily: 'Fraunces, serif',
          fontSize: '48px',
          color: '#ecfeff',
        }
      )
      .setOrigin(0.5);

    const definitions = [
      {
        label: t('common.resume'),
        action: () => {
          this.hidePause();
          this.game.events.emit('ui:resume');
        },
      },
      {
        label: t('common.restart'),
        action: () => {
          this.game.events.emit('ui:restart');
        },
      },
      {
        label: t('common.exitToMenu'),
        action: () => {
          this.game.events.emit('ui:exit');
        },
      },
    ];

    const buttons = definitions.map((definition, index) =>
      this.makeOverlayButton(
        width / 2,
        height / 2 - 40 + index * 70,
        definition.label,
        definition.action
      )
    );

    this.pauseOverlay.add([
      dim,
      title,
      ...buttons.flat(),
    ]);
  }

  setupVictoryOverlay(width, height) {
    this.victoryOverlay = this.add
      .container(0, 0)
      .setDepth(1000)
      .setVisible(false);

    const dim = this.add.rectangle(
      width / 2,
      height / 2,
      width,
      height,
      0x020617,
      0.75
    );

    this.vTitle = this.add
      .text(
        width / 2,
        height / 2 - 160,
        t('level.victory'),
        {
          fontFamily: 'Fraunces, serif',
          fontSize: '48px',
          color: '#ecfeff',
        }
      )
      .setOrigin(0.5);

    this.vStats = this.add
      .text(width / 2, height / 2 - 80, '', {
        fontFamily: 'Fraunces, serif',
        fontSize: '22px',
        color: '#a5f3fc',
        align: 'center',
      })
      .setOrigin(0.5);

    const definitions = [
      {
        label: t('common.retry'),
        action: () => {
          this.game.events.emit('ui:restart');
        },
      },
      {
        label: t('common.continue'),
        action: () => {
          this.victoryOverlay.setVisible(false);
          this.game.events.emit('ui:exit');
        },
      },
    ];

    const buttons = definitions.map((definition, index) =>
      this.makeOverlayButton(
        width / 2,
        height / 2 + 30 + index * 70,
        definition.label,
        definition.action
      )
    );

    this.victoryOverlay.add([
      dim,
      this.vTitle,
      this.vStats,
      ...buttons.flat(),
    ]);
  }

  setupDefeatOverlay(width, height) {
    this.defeatOverlay = this.add
      .container(0, 0)
      .setDepth(1000)
      .setVisible(false);

    const dim = this.add.rectangle(
      width / 2,
      height / 2,
      width,
      height,
      0x020617,
      0.75
    );

    const title = this.add
      .text(
        width / 2,
        height / 2 - 100,
        t('level.defeat'),
        {
          fontFamily: 'Fraunces, serif',
          fontSize: '48px',
          color: '#fca5a5',
        }
      )
      .setOrigin(0.5);

    const definitions = [
      {
        label: t('level.retryFromCheckpoint'),
        action: () => {
          this.hideDefeat();
          this.game.events.emit(
            'ui:retryCheckpoint'
          );
        },
      },
      {
        label: t('common.exit'),
        action: () => {
          this.game.events.emit('ui:exit');
        },
      },
    ];

    const buttons = definitions.map((definition, index) =>
      this.makeOverlayButton(
        width / 2,
        height / 2 + 20 + index * 70,
        definition.label,
        definition.action
      )
    );

    this.defeatOverlay.add([
      dim,
      title,
      ...buttons.flat(),
    ]);
  }

  makeOverlayButton(x, y, label, action) {
    const background = this.add
      .rectangle(
        x,
        y,
        320,
        54,
        0x0e7490,
        0.9
      )
      .setStrokeStyle(2, 0x22d3ee, 1)
      .setInteractive({
        useHandCursor: true,
      });

    // El texto no es interactivo. El rectángulo controla toda la pulsación.
    const text = this.add
      .text(x, y, label, {
        fontFamily: 'Fraunces, serif',
        fontSize: '22px',
        color: '#ecfeff',
      })
      .setOrigin(0.5);

    const resetAppearance = () => {
      background
        .setFillStyle(0x0e7490, 0.9)
        .setScale(1);
    };

    background.on('pointerover', () => {
      background.setFillStyle(0x0891b2, 1);
    });

    background.on('pointerout', () => {
      resetAppearance();
    });

    background.on('pointerdown', () => {
      background
        .setFillStyle(0x0891b2, 1)
        .setScale(0.97);
    });

    // Ejecutar en pointerup es más fiable que pointerdown en Android.
    background.on('pointerup', () => {
      resetAppearance();
      action();
    });

    background.on('pointerupoutside', () => {
      resetAppearance();
    });

    return [background, text];
  }

  showPause() {
    this.releaseAllTouchControls();
    this.pauseOverlay.setVisible(true);
  }

  hidePause() {
    this.pauseOverlay.setVisible(false);
  }

  showVictory(stats) {
    this.releaseAllTouchControls();

    this.vStats.setText(
      `${t('level.remainingVolume')}: ${Math.round(stats.volume)}\n` +
      `${t('level.dropsCollected')}: ${stats.drops}`
    );

    this.victoryOverlay.setVisible(true);
  }

  showDefeat() {
    this.releaseAllTouchControls();
    this.defeatOverlay.setVisible(true);
  }

  hideDefeat() {
    this.defeatOverlay.setVisible(false);
  }

  handleResize() {
    // El proyecto usa Scale.FIT. El reposicionamiento dinámico
    // puede añadirse más adelante si cambia el sistema de escala.
  }

  cleanup() {
    this.releaseAllTouchControls();

    if (this.globalPointerUpHandler) {
      this.input.off(
        'pointerup',
        this.globalPointerUpHandler
      );
    }

    if (this.gameOutHandler) {
      this.input.off('gameout', this.gameOutHandler);
    }

    this.scale.off('resize', this.handleResize, this);

    this.game.events.off(
      'volume:update',
      this.onVolumeUpdate
    );

    this.game.events.off(
      'drops:update',
      this.onDropsUpdate
    );

    this.game.events.off(
      'ui:toast',
      this.onToast
    );

    this.game.events.off(
      'ui:showTouchControls',
      this.onShowTouchControls
    );

    this.game.events.off(
      'level:victory',
      this.onVictory
    );

    this.game.events.off(
      'level:defeat',
      this.onDefeat
    );

    this.game.events.off(
      'ui:showPause',
      this.onShowPause
    );

    this.game.events.off(
      'ui:hidePause',
      this.onHidePause
    );

    this.game.events.off(
      'ui:hideDefeat',
      this.onHideDefeat
    );
  }
}